import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Decrypt, Encrypt } from "../utils/Functions";
import {
  bundleCheckoutInfoToPlayerValues,
  getBundlePlayerDisplayName,
  getBundleTeamDisplayValue,
} from "../utils/cartLineHelpers";
import styles from "../../scss/common/common.module.scss";

const EditPencilIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M21.2799 6.40005L11.7399 15.94C10.7899 16.89 7.96987 17.33 7.33987 16.7C6.70987 16.07 7.13987 13.25 8.08987 12.3L17.6399 2.75002C17.8754 2.49308 18.1605 2.28654 18.4781 2.14284C18.7956 1.99914 19.139 1.92124 19.4875 1.9139C19.8359 1.90657 20.1823 1.96991 20.5056 2.10012C20.8289 2.23033 21.1225 2.42473 21.3686 2.67153C21.6147 2.91833 21.8083 3.21243 21.9376 3.53609C22.0669 3.85976 22.1294 4.20626 22.1211 4.55471C22.1128 4.90316 22.0339 5.24635 21.8894 5.5635C21.7448 5.88065 21.5375 6.16524 21.2799 6.40005V6.40005Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11 4H6C4.93913 4 3.92178 4.42142 3.17163 5.17157C2.42149 5.92172 2 6.93913 2 8V18C2 19.0609 2.42149 20.0783 3.17163 20.8284C3.92178 21.5786 4.93913 22 6 22H17C19.21 22 20 20.2 20 18V13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SaveTickIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19.7071 6.29289C20.0976 6.68342 20.0976 7.31658 19.7071 7.70711L10.4142 17C9.63316 17.7811 8.36683 17.781 7.58579 17L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929C3.68342 10.9024 4.31658 10.9024 4.70711 11.2929L9 15.5858L18.2929 6.29289C18.6834 5.90237 19.3166 5.90237 19.7071 6.29289Z"
      fill="#0F1729"
    />
  </svg>
);

const CancelCrossIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="#0F1729"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type EditingField = "playerName" | "team" | null;

type Props = {
  cartIndex: number;
  bundleCheckoutInfo: any;
  reLoadCart: boolean;
  setReLoadCart: (value: boolean) => void;
  editable?: boolean;
};

function splitPlayerName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
}

const BundleCheckoutInfoSummary = ({
  cartIndex,
  bundleCheckoutInfo,
  reLoadCart,
  setReLoadCart,
  editable = true,
}: Props) => {
  const storeData = useSelector((state: any) => state.storeDataSlice.data);
  const [editingField, setEditingField] = useState<EditingField>(null);
  const [draftPlayerName, setDraftPlayerName] = useState("");
  const [draftTeamId, setDraftTeamId] = useState("");

  const playerName = getBundlePlayerDisplayName(bundleCheckoutInfo);
  const teamName = getBundleTeamDisplayValue(storeData, bundleCheckoutInfo?.teamId);

  const teamOptions = useMemo(
    () =>
      storeData?.teams?.map((team: any) => ({
        value: team.id.toString(),
        label: team.team_name,
      })) ?? [],
    [storeData]
  );

  const persistCheckoutInfo = (updates: {
    firstName?: string;
    lastName?: string;
    teamId?: string;
  }) => {
    const cartDataInfoD = localStorage?.getItem("cart");
    if (!cartDataInfoD) return;

    const cartData = Decrypt(cartDataInfoD);
    const cartDataInfo = JSON.parse(cartData);
    if (!cartDataInfo[cartIndex]) return;

    const current = bundleCheckoutInfoToPlayerValues(
      cartDataInfo[cartIndex].bundle_checkout_info
    );
    cartDataInfo[cartIndex].bundle_checkout_info = {
      ...current,
      ...updates,
    };
    localStorage.setItem("cart", Encrypt(JSON.stringify(cartDataInfo)));
    setReLoadCart(!reLoadCart);
    setEditingField(null);
  };

  const startEditing = (field: EditingField) => {
    if (!editable || field == null) return;
    setEditingField(field);
    if (field === "playerName") {
      setDraftPlayerName(playerName === "—" ? "" : playerName);
    }
    if (field === "team") {
      setDraftTeamId(bundleCheckoutInfo?.teamId?.toString() ?? "");
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
  };

  const savePlayerName = () => {
    const { firstName, lastName } = splitPlayerName(draftPlayerName);
    persistCheckoutInfo({ firstName, lastName });
  };

  const saveTeam = () => {
    persistCheckoutInfo({ teamId: draftTeamId });
  };

  return (
    <div className={styles.bundleCheckoutInfo}>
      <p className={styles.bundleCheckoutInfoRow}>
        <strong>Player Name</strong>:{" "}
        {editable && editingField === "playerName" ? (
          <span className={styles.bundleCheckoutInfoEditGroup}>
            <input
              type="text"
              className={styles.bundleCheckoutInfoInput}
              value={draftPlayerName}
              onChange={(e) => setDraftPlayerName(e.target.value)}
            />
            <button
              type="button"
              className={styles.bundleCheckoutInfoAction}
              aria-label="Save player name"
              onClick={savePlayerName}
            >
              <SaveTickIcon />
            </button>
            <button
              type="button"
              className={styles.bundleCheckoutInfoAction}
              aria-label="Cancel editing player name"
              onClick={cancelEditing}
            >
              <CancelCrossIcon />
            </button>
          </span>
        ) : (
          <>
            {playerName}
            {editable ? (
              <button
                type="button"
                className={styles.bundleCheckoutInfoEdit}
                aria-label="Edit player name"
                onClick={() => startEditing("playerName")}
              >
                <EditPencilIcon />
              </button>
            ) : null}
          </>
        )}
      </p>
      <p className={styles.bundleCheckoutInfoRow}>
        <strong>Select Team</strong>:{" "}
        {editable && editingField === "team" ? (
          <span className={styles.bundleCheckoutInfoEditGroup}>
            <select
              className={styles.bundleCheckoutInfoSelect}
              value={draftTeamId}
              onChange={(e) => setDraftTeamId(e.target.value)}
            >
              <option value="">Select</option>
              {teamOptions.map((team: { value: string; label: string }) => (
                <option key={team.value} value={team.value}>
                  {team.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={styles.bundleCheckoutInfoAction}
              aria-label="Save team"
              onClick={saveTeam}
            >
              <SaveTickIcon />
            </button>
            <button
              type="button"
              className={styles.bundleCheckoutInfoAction}
              aria-label="Cancel editing team"
              onClick={cancelEditing}
            >
              <CancelCrossIcon />
            </button>
          </span>
        ) : (
          <>
            {teamName}
            {editable ? (
              <button
                type="button"
                className={styles.bundleCheckoutInfoEdit}
                aria-label="Edit team"
                onClick={() => startEditing("team")}
              >
                <EditPencilIcon />
              </button>
            ) : null}
          </>
        )}
      </p>
    </div>
  );
};

export default BundleCheckoutInfoSummary;
